import './Comment.sass';

import React, { FC, useCallback, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router5';
import copyToClipboard from 'copy-text-to-clipboard';
import { LongPressDetectEvents, useLongPress } from 'use-long-press';

import { CommentProps } from './Comment.interface';
import {
  useCurrentLang,
  useCurrentUserInfo,
  useDate,
  useDecryptKey,
  useDesktop,
  useGroupInfo,
  useLikesCount,
  usePost,
  usePosts,
  useTranslator,
  useUserInfo,
  useVisibleElement,
} from '$hooks';
import { GROUP, PROFILE } from '$shared/constants/pages';
import {
  Attachments,
  Loader,
  Truncate,
  UnitName,
  UnitPhoto,
  Verified,
} from '$shared/components';
import {
  IcCopy28,
  IcDropdown16,
  IcLike24,
  IcMore24,
  IcPin24,
  IcReport24,
  IcUnpin28,
} from '$assets';
import { classNames, classNames as cn, isOnlyEmoji, transliterate } from '$utils';
import { postsActions, postsSelector } from '$store/posts';
import { useDispatch, useSelector } from 'react-redux';
import { ActionBottomSheet, AnimatedEmoji, Button } from '$uikit';
import { incline } from 'lvovich';
import { blacklistActions, blacklistSelector } from '$store/blacklist';
import { ReportReason } from '$shared/constants/reportReason';
import { toastActions } from '$store/toast';
import { PostPlaceholder } from '$shared/components/Post/PostPlaceholder/PostPlaceholder';
import { AuthPopoverState, PostType } from '$store/models';
import { profileActions } from '$store/profile';
import { ActionBottomSheetOption } from '$uikit/ActionBottomSheet/ActionBottomSheet.interface';
import { showModal } from '$navigation/helper';
import { LargeEmoji } from '$assets/mapping';

enum CommentActionsGroups {
  MAIN = 'main',
  ALERT = 'alert',
}

export const Comment: FC<CommentProps> = ({
  rootPostId,
  commentId,
  isFullPage = false,
}) => {
  const comment = usePost(commentId);
  const author = useUserInfo(comment.publisherId);
  const group = useGroupInfo(-comment.ownerId);
  const userUnit = comment.ownerId < 0 ? group : author;
  const currentLang = useCurrentLang();
  const isRU = currentLang.key === 'ru';
  const isDesktop = useDesktop();
  const date = useDate(comment.createdAt);
  const dispatch = useDispatch();
  const [isMenuShown, setMenuShown] = useState(false);
  const [isTextExpanded, setTextExpanded] = useState(
    isFullPage || comment.text.length <= 200,
  );
  const curUser = useCurrentUserInfo();
  const [touched, setTouched] = useState(false);
  const post = usePost(comment.parentId);
  const replyToUser = useUserInfo(comment.replyTo?.ownerId ?? 0);
  const replyToGroup = useGroupInfo(Math.abs(comment.replyTo?.ownerId ?? 0));
  const postsState = useSelector(postsSelector);
  const repliesState = postsState.replies[commentId] || {};
  const replies = usePosts(`comments_${commentId}`);
  const rootPost = usePost(rootPostId);
  const postGroup = useGroupInfo(rootPost ? -rootPost.ownerId : 0);
  const { groupBan, banFromMe } = useSelector(blacklistSelector);
  const likesCount = useLikesCount(comment.likes);
  const decryptKey = useDecryptKey(comment);
  const canView = decryptKey || comment.type === PostType.FREE;
  const t = useTranslator();
  const elem = useRef<HTMLDivElement>(null);
  const { visible } = useVisibleElement(elem, false, 100);

  const isAuthor = comment.publisherId === curUser?.id;
  const authorLabel =
    comment.replyTo?.postId === comment.parentId &&
    comment.parentId !== rootPost.id &&
    comment.ownerId === rootPost.ownerId;
  const isPostAuthor = post.ownerId == curUser?.id;
  const canPin =
    comment.level === 1 && postGroup
      ? postGroup.isAdmin || postGroup?.isModer
      : rootPost.ownerId === curUser.id;

  let canDelete = isAuthor || isPostAuthor;
  if (rootPost && rootPost.ownerId < 0 && postGroup && postGroup.isModer) {
    canDelete = true;
  }

  const detectActionButton = useCallback(
    (e) =>
      e?.target.closest('.Comment__replies_actions') ||
      e?.target.closest('.Comment__reply') ||
      e?.target.closest('.Comment__like'),
    [],
  );

  const authorInfo = useMemo(() => {
    if (comment.ownerId < 0) {
      return {
        photo: group.photo,
        route: GROUP,
      };
    } else {
      return {
        hasNftPhoto: author.hasNftPhoto,
        photo: author.photo,
        route: PROFILE,
      };
    }
  }, [group, author]);

  const isEmoji = useMemo(() => {
    return (
      !!comment &&
      isOnlyEmoji(comment.text) &&
      comment.text.length < 15 &&
      comment.attachments?.length === 0
    );
  }, [comment]);

  const largeEmoji = useMemo(() => {
    if (!comment || (comment.attachments && comment.attachments.length > 0)) {
      return null;
    }
    const text = comment.text.trim();
    const supportedEmoji = LargeEmoji[text];
    return supportedEmoji && <AnimatedEmoji emoji={text} />;
  }, [visible, comment]);

  const handleRetry = useCallback(() => {
    dispatch(postsActions.replyComment({ parentId: rootPostId, comment }));
    const commentForm = document.getElementById('comment_form');
    commentForm && commentForm.focus();
  }, [dispatch]);

  const handleClick = useCallback(
    (e) => {
      setTouched(false);
      if (e.type === 'touchmove' || detectActionButton(e)) {
        return;
      }

      if (!isTextExpanded) {
        setTextExpanded(true);
        return;
      }

      showModal('post' + comment.id);
    },
    [
      dispatch,
      setTouched,
      isTextExpanded,
      comment.id,
      curUser.id,
      comment.ownerId,
      comment.level,
    ],
  );

  const handleClickMore = useCallback(
    (e) => {
      if (detectActionButton(e)) return;
      e?.stopPropagation();
      setMenuShown(true);
    },
    [dispatch, setMenuShown],
  );

  const handleLike = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      if (curUser.id) {
        dispatch(postsActions.toggleLike(commentId));
      } else {
        dispatch(profileActions.setAuthPopoverState(AuthPopoverState.LOGIN));
      }
    },
    [dispatch, commentId, curUser.id],
  );

  const handleMenu = useCallback(
    (value) => {
      setMenuShown(false);
      if (!curUser.id) {
        dispatch(profileActions.setAuthPopoverState(AuthPopoverState.LOGIN));
        return;
      }
      if (value === 'pin') {
        dispatch(postsActions.pinPost(commentId));
      } else if (value === 'unpin') {
        dispatch(postsActions.unpinPost(commentId));
      } else if (value === 'copyText') {
        copyToClipboard(comment.text);
        dispatch(toastActions.setToastSuccess(t('copied')));
      } else if (value === 'delete') {
        dispatch(postsActions.deletePost(commentId));
      } else if (Object.values(ReportReason).includes(value)) {
        dispatch(
          postsActions.reportComment({
            commentId,
            reason: value,
          }),
        );
      }
    },
    [dispatch, commentId, setMenuShown, comment, t],
  );

  const handleRestore = useCallback(() => {
    dispatch(postsActions.restorePost(commentId));
  }, [dispatch, commentId]);

  const handleBlockAuthor = useCallback(() => {
    if (rootPost?.ownerId < 0) {
      dispatch(
        blacklistActions.groupBan({
          userId: comment.ownerId,
          groupId: rootPost?.ownerId < 0 ? -rootPost?.ownerId : 0,
        }),
      );
    } else {
      dispatch(blacklistActions.ban(comment.ownerId));
    }
  }, [rootPost, comment]);

  const handleUnblockAuthor = useCallback(() => {
    if (rootPost?.ownerId < 0) {
      dispatch(
        blacklistActions.groupUnban({
          userId: comment.ownerId,
          groupId: rootPost?.ownerId < 0 ? -rootPost?.ownerId : 0,
        }),
      );
    } else {
      dispatch(blacklistActions.unban(comment.ownerId));
    }
  }, [rootPost, comment]);

  const isBanned = useMemo(() => {
    if (rootPost?.ownerId < 0) {
      const groupId = -rootPost?.ownerId;
      if (!groupBan[groupId]) {
        return false;
      }

      return groupBan[groupId]![comment.ownerId];
    } else {
      return banFromMe[comment.ownerId];
    }
  }, [rootPost, banFromMe, groupBan, comment]);

  const replyToLabel = useMemo(() => {
    if (comment.replyTo) {
      const name =
        comment.replyTo.ownerId < 0 ? replyToGroup?.name : replyToUser?.firstName;
      return isRU
        ? incline(
            {
              first: name ?? '',
            },
            'dative',
          ).first
        : transliterate(name);
    }
    return null;
  }, [isRU, comment.replyTo, replyToUser]);

  const loadReplies = useCallback(() => {
    dispatch(
      postsActions.loadReplies({
        postId: comment.id,
      }),
    );
  }, [dispatch, comment.id]);

  const hideReplies = useCallback(() => {
    dispatch(
      postsActions.toggleReplies({
        postId: comment.id,
        isShown: false,
      }),
    );
  }, [dispatch, comment]);

  const showReplies = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.stopPropagation();
      if (replies.length === comment.comments) {
        dispatch(
          postsActions.toggleReplies({
            postId: comment.id,
            isShown: true,
          }),
        );
      } else {
        dispatch(
          postsActions.loadReplies({
            postId: comment.id,
          }),
        );
      }
    },
    [dispatch, comment, replies],
  );

  function renderReplies() {
    if (comment.comments === 0) {
      return null;
    }

    return (
      <div className="Comment__replies">
        {repliesState.isShown &&
          replies.map((commentId) => (
            <Comment key={commentId} rootPostId={rootPostId} commentId={commentId} />
          ))}
        {repliesState.isLoading ? (
          <div className="Comment__replies_loading">
            <div className="Comment__replies_loading__in">
              <Loader />
            </div>
          </div>
        ) : (
          <div
            className={cn('Comment__replies_actions', { isShown: repliesState.isShown })}
          >
            {!repliesState.isShown && (
              <div className="Comment__replies_button" onClick={showReplies}>
                <IcDropdown16 />
                {t('replies_count', { count: comment.comments })}
              </div>
            )}
            {repliesState.isShown && repliesState.nextFrom ? (
              <div className="Comment__replies_button" onClick={loadReplies}>
                <IcDropdown16 />
                {t('show_more')}
              </div>
            ) : (
              <div />
            )}
            {repliesState.isShown && replies.length > 0 && (
              <div className="Comment__replies_button hide" onClick={hideReplies}>
                {t('comment_collapse')}
                <IcDropdown16 />
              </div>
            )}
          </div>
        )}
      </div>
    );
  }

  const longPressBindProps = useLongPress<HTMLDivElement>(
    !isDesktop && curUser.id ? handleClickMore : null,
    {
      onStart: (e) => {
        e?.stopPropagation();
        if (detectActionButton(e)) return;
        setTouched(true);
      },
      onFinish: (e) => {
        e?.stopPropagation();
        setTouched(false);
      },
      onCancel: handleClick,
      cancelOnMovement: true,
      captureEvent: true,
      detect: LongPressDetectEvents.BOTH,
    },
  );

  if (comment.isDeleted) {
    return (
      <div className="Comment Comment__deleted">
        <div className="Comment__deleted__label">{t('comment_deleted')}</div>
        <div className="Comment__deleted__actions">
          {!isAuthor &&
            comment.ownerId > 0 &&
            (isBanned ? (
              <Button mode="secondary" size="small" onClick={handleUnblockAuthor}>
                {t('comment_unblock_author')}
              </Button>
            ) : (
              <Button mode="secondary" size="small" onClick={handleBlockAuthor}>
                {t('comment_block_author')}
              </Button>
            ))}
          <Button mode="secondary" size="small" onClick={handleRestore}>
            {t('comment_restore')}
          </Button>
        </div>
      </div>
    );
  }

  const options: ActionBottomSheetOption[] = [];

  if (canPin) {
    options.push(
      comment.pinned
        ? {
            label: t('comment_unpin'),
            value: 'unpin',
            groupId: CommentActionsGroups.MAIN,
            icon: <IcUnpin28 />,
          }
        : {
            label: t('comment_pin'),
            value: 'pin',
            icon: <IcPin24 />,
            groupId: CommentActionsGroups.MAIN,
          },
    );
  }

  options.push({
    label: t('comment_copy_text'),
    value: 'copyText',
    icon: <IcCopy28 />,
    groupId: CommentActionsGroups.MAIN,
  });

  if (!isAuthor) {
    options.push({
      label: t('message_report'),
      value: 'report',
      icon: <IcReport24 />,
      options: [
        {
          label: t('post_action_report_spam'),
          value: ReportReason.SPAM,
          groupId: CommentActionsGroups.ALERT,
        },
        {
          label: t('post_action_report_offensive'),
          value: ReportReason.OFFENSIVE,
          groupId: CommentActionsGroups.ALERT,
        },
        {
          label: t('post_action_report_false_info'),
          value: ReportReason.FALSE_INFO,
          groupId: CommentActionsGroups.ALERT,
        },
      ],
    });
  }

  if (canDelete) {
    options.push({
      label: t('comment_delete'),
      value: 'delete',
      mode: 'alert',
      groupId: CommentActionsGroups.ALERT,
    });
  }

  return (
    <>
      <ActionBottomSheet
        title={t('comment_actions_title')}
        isOpened={isMenuShown}
        options={options}
        onChange={handleMenu}
        onClose={() => setMenuShown(false)}
      />
      <div
        ref={elem}
        className={cn('Comment', { touched })}
        {...(isDesktop ? { onClick: handleClick } : longPressBindProps)}
      >
        <UnitPhoto unit={comment.ownerId < 0 ? group : author} size="extra-small" />
        <div className="Comment__content">
          <div className="Comment__name_wrap">
            <Link
              routeName={authorInfo.route}
              routeParams={{ id: Math.abs(comment.ownerId) }}
            >
              <div className="Comment__name">
                <UnitName unit={userUnit} />
              </div>
            </Link>
            <Verified objectId={comment.ownerId} />
            {comment.pinned && (
              <div className="Comment__name__pinned">
                <IcPin24 />
              </div>
            )}
            {authorLabel && (
              <div className={'Comment__name__author_label'}>{t('comment_author')}</div>
            )}
            {comment.replyTo && !authorLabel && (
              <div className="Comment__name__reply">
                ‣&nbsp;
                <Link
                  className="Comment__name__reply__user"
                  routeName={comment.replyTo.ownerId > 0 ? PROFILE : GROUP}
                  routeParams={{ id: Math.abs(comment.replyTo.ownerId) }}
                >
                  {replyToLabel}
                </Link>
              </div>
            )}
            {!!options.length && (
              <div onClick={handleClickMore} className="Comment__more">
                <IcMore24 />
              </div>
            )}
          </div>
          {comment.text.length > 0 && (
            <div
              className={classNames({
                Comment__text: true,
                emoji: isEmoji,
              })}
            >
              {largeEmoji ? (
                largeEmoji
              ) : (
                <Truncate
                  onExpand={() => setTextExpanded(true)}
                  lines={12}
                  limit={isTextExpanded ? 0 : 200}
                  text={comment.text}
                  expandText={t('post_text_expand')}
                />
              )}
            </div>
          )}
          <PostPlaceholder post={comment}>
            <Attachments
              decryptKey={decryptKey}
              forceBlur={!canView}
              attachments={comment.attachments}
              parentObject={`comment_${commentId}`}
            />
          </PostPlaceholder>
          <div className="Comment__footer">
            <div className="Comment__footer__group">
              <div className="Comment__date">{date}</div>
              <div onClick={handleRetry} className="Comment__reply">
                {t('comment_reply_btn')}
              </div>
            </div>
            <div
              className={cn({
                Comment__like: true,
                liked: comment.isLiked,
              })}
              onClick={handleLike}
            >
              <div className="Comment__like_icon">
                <IcLike24 />
              </div>
              {comment.likes > 0 && (
                <div className="Comment__like__count">{likesCount}</div>
              )}
            </div>
          </div>
          {renderReplies()}
        </div>
      </div>
    </>
  );
};
